<template>
  <div
    class="app-toast-host pointer-events-none fixed z-50 flex w-full flex-col items-center"
    :style="hostStyle"
    aria-live="polite"
  >
    <Transition name="app-toast">
      <article
        v-if="toastState.items[0]"
        :key="toastState.items[0].id"
        class="app-toast pointer-events-auto"
        :class="[
          `app-toast--${toastState.items[0].type}`,
          toastState.items[0].bumpCount ? `app-toast--bump-${toastState.items[0].bumpCount}` : '',
        ]"
        role="status"
        @click="dismiss(toastState.items[0].id)"
      >
        <span class="app-toast-dot" aria-hidden="true">
          <span class="app-toast-dot-ring" />
        </span>
        <span class="app-toast-message">{{ toastState.items[0].message }}</span>
      </article>
    </Transition>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { toastState, removeAppToast } from '@/utils/appToast'

const route = useRoute()

const COMPACT_TOP_ROUTES = new Set([
  'LoginView',
  'RegisterView',
  'ForgotPasswordView',
  'le',
  'lg',
  'InvitationListView',
  'EsimView',
])

const TALL_HEADER_ROUTES = new Set([
  'AddressView',
  'AddAddressView',
  'PaymentStatusView',
])

function getHeaderOffset(routeName) {
  if (COMPACT_TOP_ROUTES.has(routeName)) return '0px'
  if (TALL_HEADER_ROUTES.has(routeName)) return '4.5rem'
  if (routeName === 'HomeView') return '3.25rem'
  if (routeName === 'PackageView') return '3.5rem'
  return '4rem'
}

const hostStyle = computed(() => ({
  '--app-toast-header-offset': getHeaderOffset(route.name),
}))

function dismiss(id) {
  removeAppToast(id)
}
</script>

<style scoped>
.app-toast-host {
  left: 50%;
  top: calc(
    0.5rem
    + max(env(safe-area-inset-top, 0px), var(--tg-content-safe-top, 0px))
    + var(--app-toast-header-offset, 4rem)
  );
  transform: translateX(-50%);
  width: min(100vw, var(--mobile-frame-max-width, 430px));
  padding: 0 1rem;
}

.app-toast {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  max-width: min(100%, 20rem);
  padding: 0.55rem 0.9rem;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  will-change: transform, opacity;
}

.app-toast:active {
  transform: scale(0.97);
  transition: transform 0.12s ease;
}

.app-toast-dot {
  position: relative;
  flex-shrink: 0;
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 9999px;
}

.app-toast-dot-ring {
  position: absolute;
  inset: -0.2rem;
  border-radius: 9999px;
  opacity: 0;
  animation: app-toast-ring 0.55s ease-out forwards;
}

.app-toast-message {
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.3;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-toast--success .app-toast-dot {
  background: #10b981;
}
.app-toast--success .app-toast-dot-ring {
  background: rgba(16, 185, 129, 0.28);
}

.app-toast--error .app-toast-dot {
  background: #ef4444;
}
.app-toast--error .app-toast-dot-ring {
  background: rgba(239, 68, 68, 0.28);
}

.app-toast--warning .app-toast-dot {
  background: #f59e0b;
}
.app-toast--warning .app-toast-dot-ring {
  background: rgba(245, 158, 11, 0.28);
}

.app-toast--info .app-toast-dot {
  background: #8b5cf6;
}
.app-toast--info .app-toast-dot-ring {
  background: rgba(139, 92, 246, 0.28);
}

/* Enter / leave */
.app-toast-enter-active {
  animation: app-toast-in 0.34s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.app-toast-enter-active .app-toast-dot {
  animation: app-toast-dot-in 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.app-toast-enter-active .app-toast-message {
  animation: app-toast-text-in 0.3s ease 0.06s both;
}

.app-toast-leave-active {
  animation: app-toast-out 0.22s ease forwards;
  pointer-events: none;
}

/* Repeat tap bump */
[class*='app-toast--bump-'] {
  animation: app-toast-bump 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes app-toast-in {
  0% {
    opacity: 0;
    transform: translateY(-12px) scale(0.92);
  }
  65% {
    opacity: 1;
    transform: translateY(2px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes app-toast-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-8px) scale(0.94);
  }
}

@keyframes app-toast-bump {
  0% { transform: scale(1); }
  40% { transform: scale(1.06); }
  100% { transform: scale(1); }
}

@keyframes app-toast-dot-in {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  70% {
    transform: scale(1.35);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes app-toast-text-in {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes app-toast-ring {
  0% {
    opacity: 0.55;
    transform: scale(0.6);
  }
  100% {
    opacity: 0;
    transform: scale(2.2);
  }
}

:global(html.dark) .app-toast {
  border-color: rgba(75, 85, 99, 0.5);
  background: rgba(31, 41, 55, 0.88);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
}

:global(html.dark) .app-toast-message {
  color: #e5e7eb;
}
</style>
